import { useRef, useState, useEffect, useCallback } from 'react';
import React from 'react';
import './index.less';
import { useParams, redirect, useNavigate, useLocation } from 'react-router-dom';
import { Html5QrcodeScanner } from "html5-qrcode";
import { Html5Qrcode } from "html5-qrcode";
import { DndContext } from '@dnd-kit/core';
import { Droppable } from './Droppable';
import { Draggable } from './Draggable';
import FramePlayer from '../../components/FramePlayer';
import ScanHash from '../../components/ScanHash';
import TrackList from '../../components/TrackList';
import axios from 'axios';
import OcTrackList from '../../components/OcTrackList';
import RecordModule from '../../components/RecordModule';
import { message } from 'antd';
import ScanOcHash from '../../components/ScanOcHash';
import { Howl } from 'howler';
import PhotoCapture from '../../components/PhotoCapture';

interface Track {
    buttonPosition: string;
    cardNumber: string;
    chineseName: string;
    description: string;
    englishName: string;
    fileDirectory: string;
    frames: number;
    hashValue: string;
    id: number;
    level: string;
    songId: number;
    path: string;
    waitForNextLoop: boolean;
    waitingToRemove?: boolean;
    hasOcImage?: boolean;
}


interface Song {
    id: number;
    englishName: string;
    chineseName: string;
    loopDuration: number;
    bpm: number;
    file_directory: string;
    type: 'original' | 'oc';
    tracks: {
        beats: Track[];
        effects: Track[];
        melodys: Track[];
        vocals: Track[];
    };
    ocTracks: OCTrack[];
}

interface OCTrack {
    hashValue: string;
    id: number;
    bpm: number;
    link: string;
    topColor?: string;
    bottomColor?: string;
    frames: 8;
    path: 'origin';
    fileDirectory: 'original';
    isRecordable?: boolean;
    firstRecordedAt?: Date;
    hasOcImage?: boolean;
}

interface GroupedSongs {
    [key: number]: Song[];
}

// 播放同步偏移：OC 录音存在约 200ms 延迟，需要提前播放来对齐

function Homepage(props: any) {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const initialBpmParam = Number(searchParams.get('bpm'));
    const initialBpm: 100 | 120 = initialBpmParam === 120 ? 120 : 100;
    const [bpm, setBpm] = useState<100|120>(initialBpm);
    
    // 如果URL中没有bpm参数，立即更新URL
    useEffect(() => {
        const currentParams = new URLSearchParams(location.search);
        if (!currentParams.get('bpm')) {
            currentParams.set('bpm', initialBpm.toString());
            // 如果没有其他参数，设置默认的songId
            if (!currentParams.get('songId') && !currentParams.get('type')) {
                // 这里暂不设置songId，等数据加载完成后再设置
            }
            const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);
    
    const html5QrCodeScannerRef = useRef<Html5Qrcode | null>(null);

    const [waitingTracks, setWaitingTracks] = useState([...Array(7)]);
    const [playingTracks, setPlayingTracks] = useState([...Array(7)]);

    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const audioElementsRef = useRef<{[key: string]: Howl}>({});
    const ocTrackIdsRef = useRef<Set<string>>(new Set());
    const [songs, setSongs] = useState<GroupedSongs>({});
    const [currentSong, setCurrentSong] = useState<Song | null>(null);
    const [currentSongIndex, setCurrentSongIndex] = useState(0);

    const [playtimestamp, setPlaytimestamp] = useState(0);
    const [loopTime, setLoopTime] = useState(9600);
    const isFirstPlayRef = useRef(true);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

    const [scannedHash, setScannedHash] = useState<string | null>(null);
    const [scannedOcHash, setScannedOcHash] = useState<string | null>(null);

    const [resetFrame, setResetFrame] = useState<number>(0);

    const [isRecordMode, setIsRecordMode] = useState(false);
    const [recordingTrack, setRecordingTrack] = useState<OCTrack | null>(null);
    const [isPhotoMode, setIsPhotoMode] = useState(false);
    const [selectedTrackHash, setSelectedTrackHash] = useState<string | null>(null);

    // 添加状态来存储计算得到的最大宽度
    const [maxWidth, setMaxWidth] = useState<number>(0);

    // 添加控制欢迎界面显示的状态
    const [showWelcome, setShowWelcome] = useState(true);

    // 触发重新拉取歌曲/音轨数据
    const [refreshKey, setRefreshKey] = useState(0);

    const [isFirstLoad, setIsFirstLoad] = useState<boolean>();

    // Add new state for generated images
    const [generatedImages, setGeneratedImages] = useState<string[]>([]);
    
    // Add state for processing hash
    const [processingHash, setProcessingHash] = useState<string | null>(null);

    // 添加锁止状态管理
    const [lockedHashes, setLockedHashes] = useState<Set<string>>(new Set());
    const [lockTimers, setLockTimers] = useState<{[key: string]: NodeJS.Timeout}>({});
    
    // 添加候选图片状态管理
    const [tracksWithCandidates, setTracksWithCandidates] = useState<Set<string>>(new Set());
    const [candidateCheckTimer, setCandidateCheckTimer] = useState<NodeJS.Timeout | null>(null);
    
    // 添加正在生成动画的音轨状态管理
    const [generatingAnimationHashes, setGeneratingAnimationHashes] = useState<Set<string>>(new Set());
    const [animationCheckTimer, setAnimationCheckTimer] = useState<NodeJS.Timeout | null>(null);

    // 初始化锁死状态 - 从localStorage恢复
    useEffect(() => {
        const initializeLockedHashes = () => {
            const lockedHashesData = JSON.parse(localStorage.getItem('lockedHashes') || '{}');
            const currentTime = Date.now();
            const lockDuration = 3 * 60 * 1000; // 3分钟
            
            const validHashes = new Set<string>();
            const newLockTimers: {[key: string]: NodeJS.Timeout} = {};
            
            Object.entries(lockedHashesData).forEach(([hash, lockTime]) => {
                const timeSinceLock = currentTime - (lockTime as number);
                
                if (timeSinceLock < lockDuration) {
                    // 锁死仍然有效
                    validHashes.add(hash);
                    
                    // 设置剩余时间的定时器
                    const remainingTime = lockDuration - timeSinceLock;
                    const timer = setTimeout(() => {
                        setLockedHashes(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(hash);
                            return newSet;
                        });
                        setLockTimers(prev => {
                            const newTimers = { ...prev };
                            delete newTimers[hash];
                            return newTimers;
                        });
                        
                        // 从localStorage中移除
                        const currentLockedHashesData = JSON.parse(localStorage.getItem('lockedHashes') || '{}');
                        delete currentLockedHashesData[hash];
                        localStorage.setItem('lockedHashes', JSON.stringify(currentLockedHashesData));
                    }, remainingTime);
                    
                    newLockTimers[hash] = timer;
                } else {
                    // 锁死已过期，从localStorage中移除
                    delete lockedHashesData[hash];
                }
            });
            
            // 更新localStorage，移除过期的锁死
            localStorage.setItem('lockedHashes', JSON.stringify(lockedHashesData));
            
            setLockedHashes(validHashes);
            setLockTimers(newLockTimers);
        };
        
        // 初始化正在生成动画的状态
        const initializeGeneratingAnimationHashes = () => {
            const generatingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
            const currentTime = Date.now();
            const animationTimeout = 5 * 60 * 1000; // 5分钟超时
            
            const validGeneratingHashes = new Set<string>();
            
            Object.entries(generatingAnimationData).forEach(([hash, startTime]) => {
                const timeSinceStart = currentTime - (startTime as number);
                
                if (timeSinceStart < animationTimeout) {
                    // 仍在生成中
                    validGeneratingHashes.add(hash);
                } else {
                    // 超时，从localStorage中移除
                    delete generatingAnimationData[hash];
                }
            });
            
            // 更新localStorage，移除超时的记录
            localStorage.setItem('generatingAnimationHashes', JSON.stringify(generatingAnimationData));
            
            setGeneratingAnimationHashes(validGeneratingHashes);
        };
        
        initializeLockedHashes();
        initializeGeneratingAnimationHashes();
        
        // 清理函数 - 组件卸载时清理所有定时器
        return () => {
            Object.values(lockTimers).forEach(timer => {
                clearTimeout(timer);
            });
        };
    }, []);

    const [isScanning, setIsScanning] = useState(false);

    // 计算最大宽度的函数
    const calculateMaxWidth = () => {
        const aspectRatio = 1080 / 810;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        
        // 根据窗口高度计算理想宽度
        const idealWidth = windowHeight * aspectRatio;
        
        // 如果理想宽度超过窗口宽度，则使用窗口宽度
        const calculatedMaxWidth = Math.min(idealWidth, windowWidth);
        
        setMaxWidth(calculatedMaxWidth);
    };

    // 初始计算和窗口大小变化时重新计算
    useEffect(() => {
        calculateMaxWidth();
        
        const handleResize = () => {
            calculateMaxWidth();
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 监听maxWidth变化，更新HTML的font-size
    useEffect(() => {
        if (maxWidth > 0) {
            document.documentElement.style.fontSize = `${maxWidth / 100}px`;
        }
    }, [maxWidth]);

    // 获取所有歌曲
    useEffect(() => {
        const fetchAllData = async () => {
            try {
                // 1. 获取所有歌曲
                const songsResponse = await axios.get('/api/v1/incredibox/songs');
                const songsData = songsResponse.data;
                
                // 创建固定长度为5的空数组
                const createEmptyArray = () => Array(5).fill(null);
                
                // 按BPM分组歌曲
                const groupedSongs: GroupedSongs = {};
                songsData.forEach((song: any) => {
                    if (!groupedSongs[song.bpm]) {
                        groupedSongs[song.bpm] = [];
                    }
                    groupedSongs[song.bpm].push({
                        ...song,
                        type: 'original',
                        tracks: {
                            beats: createEmptyArray(),
                            effects: createEmptyArray(),
                            melodys: createEmptyArray(),
                            vocals: createEmptyArray()
                        }
                    });
                });
                
                // 2. 获取所有音轨
                const savedHashes = localStorage.getItem('scanedhash');
                let tracksData: Track[] = [];
                if (savedHashes) {
                    const hashList = JSON.parse(savedHashes);
                    if (hashList.length > 0) {
                        const tracksResponse = await axios.get(`/api/v1/incredibox/tracks-by-hash`, {
                            params: {
                                hashes: JSON.stringify(hashList)
                            }
                        });
                        tracksData = tracksResponse.data;
                    }
                }
                
                // 3. 获取所有OC音轨
                const savedOcHashes = localStorage.getItem('scanedOcHash');
                let ocTracksData: OCTrack[] = [];
                if (savedOcHashes) {
                    const ocHashList = JSON.parse(savedOcHashes);
                    if (ocHashList.length > 0) {
                        const ocTracksResponse = await axios.get(`/api/v1/incredibox/oc/hashes`, {
                            params: {
                                hashes: JSON.stringify(ocHashList)
                            }
                        });
                        ocTracksData = ocTracksResponse.data;

                        // console.log('ocTracksData', ocTracksData)

                    ocTracksData = ocTracksData.map(track => ({
                        ...track,
                        fileDirectory: track.fileDirectory || 'original',
                        path: track.path || 'origin',
                        frames: track.frames || 8
                    }));
                    }
                }
                
                // 4. 整理音轨数据到对应的歌曲中
                tracksData.forEach((track: Track) => {
                    // 找到对应的歌曲
                    Object.values(groupedSongs).forEach(songList => {
                        songList.forEach((song: Song) => {
                            if (song.id === track.songId) {
                                const position = parseInt(track.buttonPosition.slice(1)) - 1;
                                if (position >= 0 && position < 5) {
                                    // 添加path属性
                                    track.path = `${song.englishName}/${track.fileDirectory}`;
                                    
                                    if (track.buttonPosition.startsWith('B')) {
                                        song.tracks.beats[position] = track;
                                    } else if (track.buttonPosition.startsWith('E')) {
                                        song.tracks.effects[position] = track;
                                    } else if (track.buttonPosition.startsWith('M')) {
                                        song.tracks.melodys[position] = track;
                                    } else if (track.buttonPosition.startsWith('V')) {
                                        song.tracks.vocals[position] = track;
                                    }
                                }
                            }
                        });
                    });
                });
                
                // 5. 添加OC音轨到歌曲列表中
                // 首先为每个BPM创建原创节奏歌曲
                [100, 120].forEach(bpm => {
                    if (!groupedSongs[bpm]) {
                        groupedSongs[bpm] = [];
                    }

                    // 检查该BPM组中是否已有原创节奏歌曲
                    const ocSongExists = groupedSongs[bpm].some(song => song.englishName === '原创节奏');

                    if (!ocSongExists) {
                        // 添加新的原创节奏歌曲
                        groupedSongs[bpm].push({
                            id: -bpm, // 使用负的BPM值作为特殊ID
                            chineseName: '',
                            englishName: '原创节奏',
                            loopDuration: 0,
                            bpm: bpm,
                            file_directory: '',
                            type: 'oc',
                            tracks: {
                                beats: Array(5).fill(null),
                                effects: Array(5).fill(null),
                                melodys: Array(5).fill(null),
                                vocals: Array(5).fill(null)
                            },
                            ocTracks: []
                        });
                    }
                });

                // 然后将OC音轨添加到对应BPM的歌曲中
                if (ocTracksData.length > 0) {
                    // console.log('OC Tracks to distribute:', ocTracksData);
                    
                    // 按BPM分组OC音轨
                    const ocTracksByBpm: { [key: number]: OCTrack[] } = {
                        100: [],
                        120: []
                    };

                    ocTracksData.forEach(track => {
                        if (track.bpm === 100 || track.bpm === 120) {
                            ocTracksByBpm[track.bpm].push(track);
                        }
                    });

                    // 将音轨添加到对应BPM的原创节奏歌曲中
                    Object.entries(ocTracksByBpm).forEach(([bpm, tracks]) => {
                        const numBpm = parseInt(bpm);
                        const songIndex = groupedSongs[numBpm].findIndex(song => song.englishName === '原创节奏');
                        if (songIndex !== -1) {
                            groupedSongs[numBpm][songIndex] = {
                                ...groupedSongs[numBpm][songIndex],
                                ocTracks: tracks
                            };
                        }
                    });
                }
                
                // 6. 设置状态
                setSongs(groupedSongs);
                
                // 设置默认歌曲
                const searchParams = new URLSearchParams(window.location.search);

                if ( !searchParams.get('songId') && !searchParams.get('type')) {
                    if (groupedSongs[bpm] && groupedSongs[bpm].length > 0) {
                        const defaultSong = groupedSongs[bpm][0];
                        setCurrentSong(defaultSong);
                        setCurrentSongIndex(0);
                        console.log('setdefault song');
                        
                        // 立即更新URL参数
                        const newParams = new URLSearchParams(window.location.search);
                        newParams.set('bpm', bpm.toString());
                        if (defaultSong.type === 'original') {
                            newParams.set('songId', defaultSong.id.toString());
                            newParams.delete('type');
                        } else {
                            newParams.delete('songId');
                            newParams.set('type', 'oc');
                        }
                        const newUrl = `${window.location.pathname}?${newParams.toString()}`;
                        window.history.replaceState({}, '', newUrl);
                    }
                } else {
                    setIsFirstLoad(true);
                }
                
            } catch (error) {
                console.error('获取数据失败:', error);
            }
        };
        
        fetchAllData();
    }, [bpm, refreshKey]); // BPM 改变或手动刷新时重新获取数据
    
    // 当BPM改变时更新默认歌曲和索引
    useEffect(() => {
        if (songs[bpm] && songs[bpm].length > 0) {
            setCurrentSong(songs[bpm][0]);
            setCurrentSongIndex(0);
        }
        setLoopTime(bpm === 100 ? 9600 : 8000);

    }, [bpm]);


    useEffect(() => {
        // console.log('currentSong', currentSong)
        // 只有在歌曲数据已加载且不是初始设置时才更新URL
        if (currentSong && Object.keys(songs).length > 0 && !isFirstLoad) {
            const searchParams = new URLSearchParams(window.location.search);
            if (currentSong?.type === 'original') {
                searchParams.set('songId', currentSong.id.toString());
                searchParams.delete('type');
            } else {
                searchParams.delete('songId');
                searchParams.set('type', 'oc');
            }
            searchParams.set('bpm', bpm.toString());
            const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
            window.history.pushState({}, '', newUrl);
        }
    }, [currentSong, songs, isFirstLoad]);

    // 根据 URL 参数 songId 或 type=oc 在歌曲加载后定位
    useEffect(() => {
        if (Object.keys(songs).length === 0) return;
        const params = new URLSearchParams(location.search);
        const songIdParam = params.get('songId');
        const typeParam = params.get('type');

        if (songIdParam) {
            const targetId = Number(songIdParam);
            if (songs[bpm]) {
                const idx = songs[bpm].findIndex(s => s.id === targetId);
                if (idx !== -1) {
                    setCurrentSongIndex(idx);
                    setCurrentSong(songs[bpm][idx]);
                    setIsFirstLoad(false); // 重置标志
                    return;
                }
            }
        }

        if (typeParam === 'oc') {
            if (songs[bpm] && songs[bpm].length > 0) {
                const ocIndex = songs[bpm].length - 1;
                setCurrentSongIndex(ocIndex);
                setCurrentSong(songs[bpm][ocIndex]);
            }
        }
        
        // 如果没有匹配的参数，也要重置标志
        setIsFirstLoad(false);

    }, [isFirstLoad, songs, bpm]);

    function onScanSuccess(decodedText:string, decodedResult:any) {
        // handle the scanned code as you like, for example:
        console.log(`Code matched = ${decodedText}`, decodedResult);
        // alert(`Code matched = ${decodedText}`);

        // Code matched = https://mixbox.pixelhouse.cn/oc/5P6AD 

        // 匹配普通轨道
        const match = decodedText.match(/^https:\/\/mixbox\.pixelhouse\.cn\/c\/(.+)$/);
        
        // 匹配OC轨道
        const ocMatch = decodedText.match(/^https:\/\/mixbox\.pixelhouse\.cn\/oc\/(.+)$/);

        if (match) {
            const hash = match[1];
            console.log(`Matched hash: ${hash}`);
            setScannedHash(hash);
            // 关闭扫码
            if (html5QrCodeScannerRef.current) {
                html5QrCodeScannerRef.current.stop().then(() => {
                    html5QrCodeScannerRef.current = null;
                    setIsScanning(false);
                }).catch((error: any) => {
                    console.error('Error stopping QR code scanner:', error);
                });
            }
        } else if (ocMatch) {
            const hash = ocMatch[1];
            console.log(`Matched OC hash: ${hash}`);
            setScannedOcHash(hash);
            // 关闭扫码
            if (html5QrCodeScannerRef.current) {
                html5QrCodeScannerRef.current.stop().then(() => {
                    html5QrCodeScannerRef.current = null;
                    setIsScanning(false);
                }).catch((error: any) => {
                    console.error('Error stopping QR code scanner:', error);
                });
            }
        } else {
            console.warn('Scanned code does not match the expected pattern.');
        }
    }
      
    function onScanFailure(error:any) {
        // handle scan failure, usually better to ignore and keep scanning.
        // for example:
        console.warn(`Code scan error = ${error}`);
    }

    // 将playAllAudio定义为组件级别的函数
    const playAllAudio = async () => {

        console.log(loopTime)

        try {
            setIsPlaying(true);
            
        // 先停止所有音频
        const audioArray = Object.values(audioElementsRef.current);
        // console.log(audioArray)
        // 暂停所有音频
        for (const audio of audioArray) {
            audio.stop();
            audio.unload();
        }

        // 等待一小段时间确保所有音频都已经停止
        await new Promise(resolve => setTimeout(resolve, 100));
            

        // 预加载所有音频
        await Promise.all(audioArray.map(audio => {
            return new Promise(resolve => {
                console.log(audio.state(),audio)
                if (audio.state() === 'loaded') {
                    resolve(null);
                } else {
                    audio.once('load', () => resolve(null));
                audio.load();
                }
            });
        }));


        // 播放所有音频
        const playPromises = audioArray.map(audio => {
            return new Promise(resolve => {
                const id = Object.entries(audioElementsRef.current).find(([k,v]) => v===audio)?.[0];
                // const isOc = id ? ocTrackIdsRef.current.has(id) : false;
                const playId:number = audio.play();
                // if (isOc) {
                //     (audio as any).seek(OC_PLAY_OFFSET_MS / 1000, playId);
                // }
                audio.once('play', () => resolve(null), playId);
            });
        });
            
        await Promise.all(playPromises).catch(err => {
            console.warn('Some audio elements could not be played:', err);
        });

        // 清除之前的 timeout
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        // 设置新的 timeout 并保存 ID
        timeoutIdRef.current = setTimeout(() => {
            // 检查并移除等待删除的音轨
            setPlayingTracks(prevTracks => {
                // 从 audioElementsRef 中移除等待删除的音轨的音频
                prevTracks.forEach(track => {
                    if (track?.waitingToRemove && track.id) {
                        console.log(track.id)
                        const audio = audioElementsRef.current[track.id];
                        if (audio) {
                            audio.stop();
                            audio.unload();
                        }
                        delete audioElementsRef.current[track.id];
                    }
                });

                // 移除等待删除的音轨并取消等待状态
                return prevTracks.map(track => 
                    track?.waitingToRemove ? undefined : 
                    track ? { ...track, waitForNextLoop: false } : undefined
                );
            });
            playAllAudio();
        }, loopTime);

        // 重置按钮显示第一帧
        setResetFrame(0);
            } catch (error) {
            console.error('Error in playAllAudio:', error);
        }
    };

    // 添加一个effect来检测用户交互
    useEffect(() => {
        const markUserInteraction = () => {
            document.body.classList.add('user-interacted');
            
            // 已经交互后可以移除这些监听器
            document.removeEventListener('click', markUserInteraction);
            document.removeEventListener('touchstart', markUserInteraction);
            document.removeEventListener('keydown', markUserInteraction);
        };
        
        document.addEventListener('click', markUserInteraction);
        document.addEventListener('touchstart', markUserInteraction);
        document.addEventListener('keydown', markUserInteraction);
        
        return () => {
            document.removeEventListener('click', markUserInteraction);
            document.removeEventListener('touchstart', markUserInteraction);
            document.removeEventListener('keydown', markUserInteraction);
        };
    }, []);

    useEffect(() => {
        playAllAudio();
    },[])        

    const addItemAtIndex = (newTrack: Track | any, index: number, isOcTrack?: boolean) => {
        console.log('Adding track at index', index, 'Track:', newTrack);
        console.log(isOcTrack)
        
        // 将加入的音轨的hash值添加到localstorage中
        const usedTracks = JSON.parse(localStorage.getItem('usedtrack') || '[]');
        if (newTrack.hashValue && !usedTracks.includes(newTrack.hashValue)) {
            usedTracks.push(newTrack.hashValue);
            localStorage.setItem('usedtrack', JSON.stringify(usedTracks));
        }

        // 创建新数组，保持7个元素的长度
        const updatedItems = [...playingTracks];

        console.log(updatedItems)
        
        // 检查整个数组中是否存在相同的音轨
        const existingIndex = updatedItems.findIndex(track => 
            track && (
                (track.cardNumber && newTrack.cardNumber && track.cardNumber === newTrack.cardNumber) || 
                (track.hashValue && newTrack.hashValue && track.hashValue === newTrack.hashValue)
            )
        );
        
        // 如果在其他位置找到相同的音轨，先删除它
        if (existingIndex !== -1 && existingIndex !== index) {
            const existingTrack = updatedItems[existingIndex];
            if (existingTrack && existingTrack.id) {
                const oldAudio = audioElementsRef.current[existingTrack.id];
                if (oldAudio) {
                    oldAudio.stop();
                    oldAudio.unload();
                    delete audioElementsRef.current[existingTrack.id];
                }
            }
            updatedItems[existingIndex] = undefined;
        }
        
        // 检查目标位置是否已有音轨
        const targetTrack = updatedItems[index];
        
        // 如果目标位置有相同的音轨，则删除
        if (targetTrack && (
            (targetTrack.cardNumber && newTrack.cardNumber && targetTrack.cardNumber === newTrack.cardNumber) || 
            (targetTrack.hashValue && newTrack.hashValue && targetTrack.hashValue === newTrack.hashValue)
        )) {
            if (targetTrack.id) {
                const oldAudio = audioElementsRef.current[targetTrack.id];
                if (oldAudio) {
                    oldAudio.stop();
                    oldAudio.unload();
                    delete audioElementsRef.current[targetTrack.id];
                }
            }
            updatedItems[index] = undefined;
            setPlayingTracks(updatedItems);
            return;
        }
        
        // 如果目标位置有不同的音轨，则停止其音频
        if (targetTrack && targetTrack.id) {
            const oldAudio = audioElementsRef.current[targetTrack.id];
            if (oldAudio) {
                oldAudio.stop();
                oldAudio.unload();
                delete audioElementsRef.current[targetTrack.id];
            }
        }

        // 创建新的track对象，添加唯一标识符
        const uniqueTrack = {
            ...newTrack,
            id: `${newTrack.cardNumber || newTrack.hashValue || 'track'}_${Date.now()}`,
            waitForNextLoop: updatedItems.filter(track => track && track.id).length !== 0  // 如果是第一个音轨则立即播放，否则等待下一个循环
        };

        // 创建新的 Howl 音频对象
        const audio = new Howl({
            src: [ isOcTrack ? newTrack.link : `https://oss.usecubes.cn/incredibox/assets/files/${newTrack.path || 'origin'}/${newTrack.fileDirectory || 'original'}_0.aac` ],
            html5: true,
            preload: true,
            loop: false,
            
            onload: () => {
                console.log('Audio loaded', uniqueTrack.id);
            },
            onplayerror: (soundId: number, err: any) => {
                console.error('Audio play error', err);
            }
        });
        
        // 替换指定位置的元素
        updatedItems[index] = uniqueTrack;
        
        const playableTracks = updatedItems.filter(track => track && track.id);
        if (playableTracks.length === 1) {
            playableTracks[0].waitForNextLoop = false;
        }

        
        // 添加新的音频到 audioElementsRef
        audioElementsRef.current[uniqueTrack.id] = audio;

        // 记录 OC 音轨 id 以便播放补偿
        if (isOcTrack) {
            ocTrackIdsRef.current.add(uniqueTrack.id);
        } else {
            ocTrackIdsRef.current.delete(uniqueTrack.id);
        }

        // 检查是否只有一个可播放的音轨
        const playableTracksCount = updatedItems.filter(track => track && track.id).length;
        if (playableTracksCount === 1) {
            playAllAudio();
        }
        
        // 更新状态
        setPlayingTracks(updatedItems);

    };


    // 添加重置功能
    const handleReset = () => {
        setIsPlaying(false);
        setResetFrame(0);
        
        // 停止所有音频
        Object.values(audioElementsRef.current).forEach(audio => {
            audio.stop();
            audio.unload();
        });
        audioElementsRef.current = {};
        
        // 清除之前的 timeout
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
        
        // 重置播放状态
        setPlayingTracks([...Array(7)]);
        setWaitingTracks([...Array(7)]);
    };

   const scanCode = async () => {
    // 扫码之前先清空当前正在播放的音轨
    clearPlayingAudio();

    try {
      const html5QrCode = new Html5Qrcode("reader");
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };
      
      // 先尝试启动相机
      await html5QrCode.start({ facingMode: "environment" }, config, onScanSuccess, onScanFailure);
      
      // 相机启动成功后再设置引用和状态
      html5QrCodeScannerRef.current = html5QrCode;
      setTimeout(() => {
          setIsScanning(true);
      }, 1600);
    } catch (error) {
      console.error('Failed to start camera:', error);
      message.error('无法启动相机，请确保已授予相机权限');
    }
   }

   const handleCloseScan = () => {
    if (html5QrCodeScannerRef.current) {
      html5QrCodeScannerRef.current.stop().then(() => {
        html5QrCodeScannerRef.current = null;
        setIsScanning(false);
      }).catch((error: any) => {
        console.error('Error stopping QR code scanner:', error);
      });
    }
   }

   const handleBPMChange = (newBPM: 100 | 120) => {
        // 重置播放状态
        setPlayingTracks([...Array(7)]);
        setBpm(newBPM);
        handleReset();
        setLoopTime(newBPM === 100 ? 9600 : 8000);
        // 清空所有模式
        setIsDeleteMode(false);
        setIsRecordMode(false);
        setIsPhotoMode(false);
        setSelectedTrackHash(null);
        setGeneratedImages([]);
        setProcessingHash(null);
    };
    

   function handleDragCancel(event:any){
    console.log(event)
   }


   function handleDragEnd(event: any) {
    const targetIndex = Number(event.over?.id);
    const sourceId = event.active.id;
    
    // 从sourceId中提取类型和索引
    const [type, index] = sourceId.split('_');
    const trackIndex = Number(index);
    
    // 根据类型获取对应的音轨
    let draggedTrack: Track | any;
    if (currentSong) {
        if (type === 'oc') {
            // 处理OC类型的track
            draggedTrack = currentSong.ocTracks[trackIndex];
            console.log('OC Track:', draggedTrack);
        } else {
            switch (type) {
                case 'beat':
                    draggedTrack = currentSong.tracks.beats[trackIndex];
                    break;
                case 'effect':
                    draggedTrack = currentSong.tracks.effects[trackIndex];
                    break;
                case 'melody':
                    draggedTrack = currentSong.tracks.melodys[trackIndex];
                    break;
                case 'vocal':
                    draggedTrack = currentSong.tracks.vocals[trackIndex];
                    break;
            }
        }
    }

    console.log('Dragged Track:', draggedTrack);
    // 如果有有效的目标位置和音轨，则添加到tracks中
    if (!isNaN(targetIndex) && draggedTrack) {
        addItemAtIndex(draggedTrack, targetIndex,type === 'oc'? true : false);
    }
}

    // 添加切换歌曲的函数
    const switchSong = (direction: 'prev' | 'next') => {
        if (!songs[bpm] || songs[bpm].length === 0) return;

        let newIndex = currentSongIndex;
        if (direction === 'prev') {
            if (currentSongIndex === 0) return; // 如果是第一首，不执行切换
            newIndex = currentSongIndex - 1;
        } else {
            if (currentSongIndex === songs[bpm].length - 1) return; // 如果是最后一首，不执行切换
            newIndex = currentSongIndex + 1;
        }

        setCurrentSongIndex(newIndex);
        setCurrentSong(songs[bpm][newIndex]);
        // 清空所有模式
        setIsDeleteMode(false);
        setIsRecordMode(false);
        setIsPhotoMode(false);
        setSelectedTrackHash(null);
        setGeneratedImages([]);
        setProcessingHash(null);
    };

    // 添加删除音轨的处理函数
    const handleDeleteTrack = async (track: Track) => {
        try {
            // 从 localStorage 中获取已扫描的 hash
            const savedHashes = localStorage.getItem('scanedhash');
            if (!savedHashes) return;

            const hashList = JSON.parse(savedHashes);
            // 过滤掉要删除的音轨的 hash
            const updatedHashes = hashList.filter((hash: string) => hash !== track.hashValue);
            
            // 更新 localStorage
            localStorage.setItem('scanedhash', JSON.stringify(updatedHashes));

            // 重新获取音轨列表
            const response = await axios.get(`/api/v1/incredibox/tracks-by-hash`, {
                params: {
                    hashes: JSON.stringify(updatedHashes)
                }
            });

            // 创建新的音轨数组，保持原有的空数组结构
            const createEmptyArray = () => Array(5).fill(null);
            const beats = createEmptyArray();
            const effects = createEmptyArray();
            const melodys = createEmptyArray();
            const vocals = createEmptyArray();

            // 按类型分类音轨并放入对应位置
            const tracks = response.data;
            tracks.forEach((track: Track) => {
                if (track.songId === currentSong?.id) {
                    const position = parseInt(track.buttonPosition.slice(1)) - 1;
                    if (position >= 0 && position < 5) {
                        if (track.buttonPosition.startsWith('B')) {
                            beats[position] = track;
                        } else if (track.buttonPosition.startsWith('E')) {
                            effects[position] = track;
                        } else if (track.buttonPosition.startsWith('M')) {
                            melodys[position] = track;
                        } else if (track.buttonPosition.startsWith('V')) {
                            vocals[position] = track;
                        }
                    }
                }
            });

            if (currentSong) {
                setCurrentSong({
                    ...currentSong,
                    tracks: {
                        beats,
                        effects,
                        melodys,
                        vocals
                    }
                });
            }

            // 退出所有模式
            setIsDeleteMode(false);
            setIsRecordMode(false);
            setIsPhotoMode(false);
            setSelectedTrackHash(null);
            setGeneratedImages([]);
            setProcessingHash(null);
        } catch (error) {
            console.error('删除音轨失败:', error);
        }
    };

    // 添加处理扫描结果的回调
    const handleScanComplete = (info?: { bpm: number; songId: number }) => {
        // 关闭弹窗
        setScannedHash(null);

        if (!info) return;

        // 切换 BPM
        const newBpm = info.bpm === 100 ? 100 : 120;
        setBpm(newBpm as 100 | 120);

        // 若歌曲列表已加载，切换到对应歌曲
        if (songs[newBpm]) {
            const idx = songs[newBpm].findIndex(s => s.id === info.songId);
            if (idx !== -1) {
                setCurrentSongIndex(idx);
                setCurrentSong(songs[newBpm][idx]);
            }
        }

        // 重新拉取音轨数据
        setRefreshKey(prev => prev + 1);
    };

    // 重置按钮动画
    useEffect(() => {
        if (isPlaying) {
            const interval = setInterval(() => {
                setResetFrame(prev => (prev + 1) % 9);
            }, loopTime / 9);
            
            return () => clearInterval(interval);
        }
    }, [isPlaying, loopTime]);

    // 处理删除按钮点击
    const handleDeleteClick = () => {
        // 关闭其他模式
        if (isRecordMode) {
            setIsRecordMode(false);
        }
        if (isPhotoMode) {
            setIsPhotoMode(false);
            setSelectedTrackHash(null);
            setGeneratedImages([]);
            setProcessingHash(null);
        }
        setIsDeleteMode(!isDeleteMode);
    };

    // 处理录音按钮点击
    const handleRecordClick = () => {
        // 如果当前是录音模式，则关闭
        if (isRecordMode) {
            setIsRecordMode(false);
            return;
        }

        // 检查是否有可录音的音轨
        const recordableTracks = currentSong?.ocTracks?.filter(track => track.isRecordable) || [];
        
        if (recordableTracks.length === 0) {
            message.error('当前无可录音音轨，请添加oc卡后重试');
            return;
        }

        // 关闭其他模式
        if (isDeleteMode) {
            setIsDeleteMode(false);
        }
        if (isPhotoMode) {
            setIsPhotoMode(false);
            setSelectedTrackHash(null);
            setGeneratedImages([]);
            setProcessingHash(null);
        }

        // 进入录音模式并提示用户
        setIsRecordMode(true);
        message.success('点击下方头像开始录音');

        // 添加GA统计事件
        if (window.gtag) {
            window.gtag('event', 'record_mode_started', {
                event_category: 'incredibox',
                event_label: 'record_mode'
            });
        }
    };

    // 处理OC音轨点击
    const handleOcTrackClick = (track: OCTrack) => {
        console.log('handleOcTrackClick called with track:', track);
        
        if (isPhotoMode) {
            // 在拍照模式下，检查hash是否被锁止
            if (isHashLocked(track.hashValue)) {
                message.warning('正在处理中，请稍后再试');
                return;
            }
            
            // 检查是否已有OC图
            if (track.hasOcImage) {
                message.warning('该音轨已生成OC图，无需重复生成');
                return;
            }
            
            // 设置选中的track hash并开启拍照模式
            setSelectedTrackHash(track.hashValue);
            return;
        }
        
        if (isRecordMode) {
            // 开始录音前清空当前播放音轨
            clearPlayingAudio();
            setRecordingTrack(track);
        }
    };

    // 处理录音模组关闭
    const handleRecordClose = () => {
        setRecordingTrack(null);
        
        setRefreshKey(prev => prev + 1);
        // 清空所有模式
        setIsRecordMode(false);
        setIsDeleteMode(false);
        setIsPhotoMode(false);
        setSelectedTrackHash(null);
        setGeneratedImages([]);
        setProcessingHash(null);
        clearPlayingAudio();

        window.location.reload();     
    };

    // 添加删除OC音轨的处理函数
    const handleDeleteOcTrack = async (track: any) => {
        try {
            // 从 localStorage 中获取已扫描的 OC hash
            const savedOcHashes = localStorage.getItem('scanedOcHash');
            if (!savedOcHashes) return;

            const hashList = JSON.parse(savedOcHashes);
            // 过滤掉要删除的音轨的 hash
            const updatedHashes = hashList.filter((hash: string) => hash !== track.hash);
            
            // 更新 localStorage
            localStorage.setItem('scanedOcHash', JSON.stringify(updatedHashes));

            // 重新获取OC音轨列表
            if (updatedHashes.length > 0) {
                const response = await axios.get(`/api/v1/incredibox/oc/hashes`, {
                    params: {
                        hashes: JSON.stringify(updatedHashes)
                    }
                });

                const ocTracksDataAll: OCTrack[] = response.data.map((track: OCTrack) => ({
                    ...track,
                    fileDirectory: track.fileDirectory || 'original',
                    path: track.path || 'origin',
                    frames: track.frames || 8
                }));

                // 仅保留与当前歌曲 BPM 相同的 OC 音轨，避免 100 / 120 串列表
                const ocTracksData = currentSong ? ocTracksDataAll.filter(t => t.bpm === currentSong.bpm) : ocTracksDataAll;

                if (currentSong) {
                    setCurrentSong({
                        ...currentSong,
                        ocTracks: ocTracksData
                    });
                }
            } else {
                // 如果没有剩余的OC音轨，则清空
                if (currentSong) {
                    setCurrentSong({
                        ...currentSong,
                        ocTracks: []
                    });
                }
            }

            // 退出所有模式
            setIsDeleteMode(false);
            setIsRecordMode(false);
            setIsPhotoMode(false);
            setSelectedTrackHash(null);
            setGeneratedImages([]);
            setProcessingHash(null);
        } catch (error) {
            console.error('删除OC音轨失败:', error);
        }
    };

    // 添加handleOcScanComplete函数
    const handleOcScanComplete = (info?: { bpm: number }) => {
        setScannedOcHash(null);

        if (!info) return;

        const newBpm = info.bpm === 100 ? 100 : 120;
        setBpm(newBpm as 100 | 120);

        // 切到该 bpm 下最后一首原创节奏歌曲
        if (songs[newBpm] && songs[newBpm].length > 0) {
            const ocIndex = songs[newBpm].length - 1; // 最后一首
            setCurrentSongIndex(ocIndex);
            setCurrentSong(songs[newBpm][ocIndex]);
        }

        // 重新拉取 OC 音轨数据
        setRefreshKey(prev => prev + 1);
    };

    // 处理欢迎界面点击的函数
    const handleWelcomeClick = () => {
        setShowWelcome(false);
        document.body.classList.add('user-interacted');
        
        // 尝试开始播放音频
        if (playingTracks.some(track => track !== undefined)) {
            playAllAudio();
        }
    };

    // 停止并清空当前播放的所有音轨
    const clearPlayingAudio = () => {
        // 停止并卸载所有音频
        Object.values(audioElementsRef.current).forEach(audio => {
            audio.stop();
            audio.unload();
        });
        audioElementsRef.current = {};
        // 清空播放中的 track 列表
        setPlayingTracks([...Array(7)]);
    };

    // 检查是否已有候选图片
    const checkExistingImages = async (hash: string): Promise<string[]> => {
        try {
            const response = await axios.get(`/api/v1/incredibox/oc/check-existing-images/${hash}`);
            if (response.data.success) {
                return response.data.data || [];
            } else {
                console.error('Failed to check existing images:', response.data.message);
                return [];
            }
        } catch (error) {
            console.error('Error checking existing images:', error);
            return [];
        }
    };

    // 检测所有OC音轨的候选图片状态
    const checkAllTracksCandidates = useCallback(async () => {
        if (!currentSong?.ocTracks) return;
        
        const newTracksWithCandidates = new Set<string>();
        
        for (const track of currentSong.ocTracks) {
            // 只检查没有OC图片的音轨
            if (!track.hasOcImage) {
                try {
                    const existingImages = await checkExistingImages(track.hashValue);
                    if (existingImages.length === 2) {
                        newTracksWithCandidates.add(track.hashValue);
                        // 如果该音轨被锁死，则解锁
                        if (lockedHashes.has(track.hashValue)) {
                            unlockHash(track.hashValue);
                        }
                    }
                } catch (error) {
                    console.error(`Error checking candidates for track ${track.hashValue}:`, error);
                }
            }
        }
        
        setTracksWithCandidates(newTracksWithCandidates);
    }, [currentSong?.ocTracks, lockedHashes]);

    // 检测正在生成动画的音轨状态
    const checkGeneratingAnimationStatus = useCallback(async () => {
        if (!currentSong?.ocTracks) return;
        
        // 获取当前的生成动画状态
        const currentGeneratingHashes = new Set(generatingAnimationHashes);
        if (currentGeneratingHashes.size === 0) return;
        
        const newGeneratingHashes = new Set<string>();
        
        for (const hash of Array.from(currentGeneratingHashes)) {
            try {
                // 检查该音轨是否已生成OC图片
                const track = currentSong.ocTracks.find(t => t.hashValue === hash);
                if (track && track.hasOcImage) {
                    // 已生成OC图片，从生成状态中移除
                    const generatingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
                    delete generatingAnimationData[hash];
                    localStorage.setItem('generatingAnimationHashes', JSON.stringify(generatingAnimationData));
                    
                    // 重新加载数据以显示OC图片
                    setRefreshKey(prev => prev + 1);
                } else {
                    // 仍在生成中
                    newGeneratingHashes.add(hash);
                }
            } catch (error) {
                console.error(`Error checking animation status for track ${hash}:`, error);
                // 出错时也保留在生成状态中
                newGeneratingHashes.add(hash);
            }
        }
        
        setGeneratingAnimationHashes(newGeneratingHashes);
    }, [currentSong?.ocTracks]);

    // 解锁hash的函数
    const unlockHash = (hash: string) => {
        setLockedHashes(prev => {
            const newSet = new Set(prev);
            newSet.delete(hash);
            return newSet;
        });
        
        // 清除定时器
        if (lockTimers[hash]) {
            clearTimeout(lockTimers[hash]);
            setLockTimers(prev => {
                const newTimers = { ...prev };
                delete newTimers[hash];
                return newTimers;
            });
        }
        
        // 从localStorage中移除
        const currentLockedHashesData = JSON.parse(localStorage.getItem('lockedHashes') || '{}');
        delete currentLockedHashesData[hash];
        localStorage.setItem('lockedHashes', JSON.stringify(currentLockedHashesData));
    };

    // 锁止hash 3分钟
    const lockHash = (hash: string) => {
        const lockTime = Date.now();
        const lockDuration = 3 * 60 * 1000; // 3分钟
        
        // 保存到localStorage
        const lockedHashesData = JSON.parse(localStorage.getItem('lockedHashes') || '{}');
        lockedHashesData[hash] = lockTime;
        localStorage.setItem('lockedHashes', JSON.stringify(lockedHashesData));
        
        setLockedHashes(prev => new Set(prev).add(hash));
        
        // 清除之前的定时器（如果存在）
        if (lockTimers[hash]) {
            clearTimeout(lockTimers[hash]);
        }
        
        // 设置3分钟后自动解锁
        const timer = setTimeout(() => {
            setLockedHashes(prev => {
                const newSet = new Set(prev);
                newSet.delete(hash);
                return newSet;
            });
            setLockTimers(prev => {
                const newTimers = { ...prev };
                delete newTimers[hash];
                return newTimers;
            });
            
            // 从localStorage中移除
            const currentLockedHashesData = JSON.parse(localStorage.getItem('lockedHashes') || '{}');
            delete currentLockedHashesData[hash];
            localStorage.setItem('lockedHashes', JSON.stringify(currentLockedHashesData));
        }, lockDuration);
        
        setLockTimers(prev => ({ ...prev, [hash]: timer }));
    };

    // 检查hash是否被锁止
    const isHashLocked = (hash: string): boolean => {
        return lockedHashes.has(hash);
    };

    // 清理定时器，防止内存泄漏
    useEffect(() => {
        return () => {
            // 组件卸载时清理所有定时器
            Object.values(lockTimers).forEach(timer => {
                clearTimeout(timer);
            });
            // 清理候选图片检测定时器
            if (candidateCheckTimer) {
                clearInterval(candidateCheckTimer);
            }
            // 清理动画状态检测定时器
            if (animationCheckTimer) {
                clearInterval(animationCheckTimer);
            }
        };
    }, [lockTimers, candidateCheckTimer, animationCheckTimer]);

    // 页面加载时检测候选图片状态，并设置30秒定时器
    useEffect(() => {
        if (currentSong?.ocTracks) {
            // 立即检测一次
            checkAllTracksCandidates();
            
            // 设置30秒定时器
            const timer = setInterval(() => {
                checkAllTracksCandidates();
            }, 30000); // 30秒
            
            setCandidateCheckTimer(timer);
        }
        
        return () => {
            if (candidateCheckTimer) {
                clearInterval(candidateCheckTimer);
            }
        };
    }, [currentSong?.ocTracks, checkAllTracksCandidates]);

    // 检测正在生成动画的音轨状态，每30秒检查一次
    useEffect(() => {
        if (currentSong?.ocTracks && generatingAnimationHashes.size > 0) {
            // 立即检测一次
            checkGeneratingAnimationStatus();
            
            // 设置30秒定时器
            const timer = setInterval(() => {
                checkGeneratingAnimationStatus();
            }, 30000); // 30秒
            
            setAnimationCheckTimer(timer);
        }
        
        return () => {
            if (animationCheckTimer) {
                clearInterval(animationCheckTimer);
            }
        };
    }, [currentSong?.ocTracks, checkGeneratingAnimationStatus]);

    // 处理拍照按钮点击
    const handlePhotoClick = async () => {
        // 如果当前是拍照模式，则关闭并清理状态
        if (isPhotoMode) {
            setIsPhotoMode(false);
            setSelectedTrackHash(null);
            setGeneratedImages([]);
            setProcessingHash(null);
            return;
        }

        // 检查是否有可拍照的音轨（未生成OC图的音轨）
        const photoableTracks = currentSong?.ocTracks?.filter(track => !track.hasOcImage) || [];
        
        if(currentSong?.ocTracks?.length === 0){
            message.error('还没有OC卡');
            return;
        }else if (photoableTracks.length === 0) {
            message.error('全都拍过照了');
            return;
        }

        // 关闭其他模式
        if (isDeleteMode) {
            setIsDeleteMode(false);
        }
        if (isRecordMode) {
            setIsRecordMode(false);
        }
        
        // 开启拍照模式并提示用户
        setIsPhotoMode(true);
        setSelectedTrackHash(null);
        message.success('点击下方头像开始拍照');

        // 添加GA统计事件
        if (window.gtag) {
            window.gtag('event', 'photo_mode_started', {
                event_category: 'incredibox',
                event_label: 'photo_mode'
            });
        }

    };

    // Modify handlePhotoCapture
    const handlePhotoCapture = async (imageData: string, hash: string) => {
        // 立即关闭摄像组件并暂存hash
        setIsPhotoMode(false);
        setSelectedTrackHash(null);
        setProcessingHash(hash);
        
        // 锁止hash 30秒
        lockHash(hash);
        
        // 显示加载提示
        message.loading('正在生成候选图片...', 0);

        try {
            // Convert base64 to blob
            const base64Data = imageData.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }
            const blob = new Blob(byteArrays, { type: 'image/jpeg' });

            // Create form data
            const formData = new FormData();
            formData.append('image', blob, 'photo.jpg');

            // Upload image and get generated images
            const response = await axios.post(`/api/v1/incredibox/oc/upload-image/${hash}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // 隐藏加载提示
            message.destroy();

            if (response.data.success) {
                setGeneratedImages(response.data.data);
                setSelectedTrackHash(hash); // 设置选中的track hash以便后续选择图片

                // 拍照生成候选图片完成统计
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'photo_generation_complete', {
                        event_category: 'photo',
                        event_label: hash || 'photo_generation',
                        value: 1
                    });
                }

            } else {
                message.error('生成图片失败');
            }
        } catch (error) {
            message.destroy();
            console.error('Error uploading photo:', error);
            message.error('上传照片失败');
        } finally {
            // 清除处理中的hash
            setProcessingHash(null);
            // 注意：不在这里解锁，让30秒定时器自动解锁
        }
    };

    // Add handleImageSelect
    const handleImageSelect = async (imageUrl: string) => {
        if (!selectedTrackHash) {
            message.error('未选择音轨');
            return;
        }

        // 锁止hash并添加到生成动画状态
        lockHash(selectedTrackHash);
        
        // 添加到正在生成动画的状态
        setGeneratingAnimationHashes(prev => new Set(prev).add(selectedTrackHash));
        
        // 保存到localStorage
        const generatingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
        generatingAnimationData[selectedTrackHash] = Date.now();
        localStorage.setItem('generatingAnimationHashes', JSON.stringify(generatingAnimationData));
        
        // Show loading message
        message.loading('正在生成动画帧...', 0);

        console.log('Selected image:', imageUrl);

        try {
            // Send request to generate animation frames
            const response = await axios.post(`/api/v1/incredibox/oc/generate-animation/${selectedTrackHash}`, {
                selectedImageUrl: imageUrl
            });

            // Hide loading message
            message.destroy();

            if (response.data.success) {
                message.success('动画帧生成成功！');
                
                // 从生成状态中移除
                setGeneratingAnimationHashes(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(selectedTrackHash);
                    return newSet;
                });
                
                // 从localStorage中移除
                const currentGeneratingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
                delete currentGeneratingAnimationData[selectedTrackHash];
                localStorage.setItem('generatingAnimationHashes', JSON.stringify(currentGeneratingAnimationData));
                
                // 重新加载ocTracks数据
                setRefreshKey(prev => prev + 1);

                // 动画帧生成完成统计
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'animation_generation_complete', {
                        event_category: 'animation',
                        event_label: selectedTrackHash || 'animation_generation',
                        value: 1
                    });
                }

            } else {
                message.error('动画帧生成失败');
                // 生成失败时也从生成状态中移除
                setGeneratingAnimationHashes(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(selectedTrackHash);
                    return newSet;
                });
                
                const currentGeneratingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
                delete currentGeneratingAnimationData[selectedTrackHash];
                localStorage.setItem('generatingAnimationHashes', JSON.stringify(currentGeneratingAnimationData));
            }
        } catch (error) {
            message.destroy();
            console.error('Error generating animation:', error);
            message.error('生成动画失败');
            
            // 出错时也从生成状态中移除
            setGeneratingAnimationHashes(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedTrackHash);
                return newSet;
            });
            
            const currentGeneratingAnimationData = JSON.parse(localStorage.getItem('generatingAnimationHashes') || '{}');
            delete currentGeneratingAnimationData[selectedTrackHash];
            localStorage.setItem('generatingAnimationHashes', JSON.stringify(currentGeneratingAnimationData));
        }

        // 清空所有相关状态
        setGeneratedImages([]);
        setIsPhotoMode(false);
        setSelectedTrackHash(null);
        setProcessingHash(null);
        setIsDeleteMode(false);
        setIsRecordMode(false);
        // 注意：不在这里解锁，让30秒定时器自动解锁
    };

    return (
        <div id="homePage">
            {/* 欢迎界面 */}
            {showWelcome && (
                <div className="welcome-overlay">
                    <div className="welcome-content">
                        <img src="/assets/images/logo.png" alt="超级节奏盒子" />
                        <h1>欢迎来到超级节奏盒子</h1>
                        <p>点击任意位置开始体验</p>
                        <button onClick={handleWelcomeClick}>开始</button>
                    </div>
                </div>
            )}
            {scannedHash && (
                <div className="scan-hash-overlay">
                    <ScanHash 
                        hash={scannedHash}
                        onCancel={handleScanComplete}
                        onCollect={handleScanComplete}
                    />
                </div>
            )}
            {scannedOcHash && (
                <div className="scan-hash-overlay">
                    <ScanOcHash 
                        hash={scannedOcHash}
                        onCancel={handleOcScanComplete}
                        onCollect={handleOcScanComplete}
                    />
                </div>
            )}
            {recordingTrack && (
                <RecordModule
                    onClose={handleRecordClose}
                    bpm={bpm}
                    ocTrack={recordingTrack}
                />
            )}
            {isPhotoMode && selectedTrackHash && (
                <PhotoCapture
                    onClose={() => {
                        setIsPhotoMode(false);
                        setSelectedTrackHash(null);
                    }}
                    onCapture={handlePhotoCapture}
                    hash={selectedTrackHash}
                />
            )}

            {generatedImages.length > 0 && (
                <div className="generated-images-overlay">
                    <div className="generated-images-content">
                        <h2>点击图片生成动画</h2>
                        <p>确定后无法修改</p>
                        <div className="images-grid">
                            {generatedImages.map((imageUrl, index) => (
                                <div 
                                    key={index} 
                                    className="image-item"
                                    onClick={() => handleImageSelect(imageUrl)}
                                >
                                    <img src={imageUrl} alt={`Generated ${index + 1}`} />
                                </div>
                            ))}
                        </div>
                        <button 
                            className="close-button"
                            onClick={() => {
                                setGeneratedImages([]);
                                setIsPhotoMode(false);
                                setSelectedTrackHash(null);
                                setProcessingHash(null);
                            }}
                        >
                            关闭
                        </button>
                    </div>
                </div>
            )}
            
            <div className="main" style={{ maxWidth: `${maxWidth}px` }}>
                <header>
                    <div className="logo">
                        <img src="/assets/images/logo.png" alt="超级节奏盒子" />
                    </div>
                    <div className="bmp">
                        <div className="changeBPM" onClick={() => handleBPMChange(100)}>
                            {bpm === 120 ? <img src="/assets/icons/arrow_left.svg" alt="" /> : null}
                        </div>
                        <div className="bmpWrap">
                            <div className="bmpNum"><p>{bpm}</p></div>
                            <div className="tip">每分钟{bpm}节拍</div>
                        </div>
                        <div className="changeBPM" onClick={() => handleBPMChange(120)}>
                            {bpm === 100 ? <img src="/assets/icons/arrow_right.svg" alt="" /> : null}
                        </div>
                    </div>
                    <div className="btns">
                        <div className="scan" onClick={scanCode}>
                            <img src="/assets/icons/scan.svg" alt="扫码" />
                        </div>
                        <div className="reset" onClick={handleReset}>
                            <FramePlayer 
                                basePath="icons/"
                                fileName="reset-*.svg"
                                frameCount={9}
                                fps={9 / (loopTime / 1000)}
                                startFrame={resetFrame}
                                hash="reset-button"
                            />
                        </div>
                    </div>
                </header>

                <DndContext onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                    <div className="stage">
                        <div className={`stageTop ${isDeleteMode ? 'delete-mode' : ''} ${isRecordMode ? 'record-mode' : ''}`} style={{
                            backgroundImage: bpm === 100 ?  
                                `url('https://oss.usecubes.cn/incredibox/assets/images/bg_100.jpg')` : 
                                `url('https://oss.usecubes.cn/incredibox/assets/images/bg_120.jpg')`
                        }}>
                        <div className="tracks">
                                {playingTracks.map((track, track_index) => {
                                    // console.log('track', track)

                                return <Droppable key={track_index} id={`${track_index}`}>
                                    {track ? (
                                            <div className={`track-content ${track.waitForNextLoop ? 'waiting' : ''} ${track.waitingToRemove ? 'blink' : ''}`} onClick={() => {
                                                const updatedItems = [...playingTracks];
                                                // 移除音频
                                                if (track.id) {
                                                    const audio = audioElementsRef.current[track.id];
                                                    if (audio) {
                                                        audio.stop();
                                                        audio.unload();
                                                        delete audioElementsRef.current[track.id];
                                                    }
                                                }
                                                // 直接移除音轨
                                                updatedItems[track_index] = undefined;
                                                setPlayingTracks(updatedItems);
                                            }}>
                                            <FramePlayer 
                                                key={`${track.id || track.hashValue}`}
                                                basePath={track.hasOcImage ? track.hashValue : `${track.path}/`}
                                                fileName={track.hasOcImage ? `${track.hashValue}-*.png` : `${track.fileDirectory}_*.svg`}
                                                frameCount={track.hasOcImage ? 2 : track.frames}
                                                fps={bpm / 60 * (track.hasOcImage ? 2 : track.frames)}
                                                topColor={track.topColor}
                                                bottomColor={track.bottomColor}
                                                zoomScale={track.hasOcImage ? 1.5 : track.zoomScale}
                                                hasOcImage={track.hasOcImage}
                                                hash={`track-${track_index}-${track.id || track.hashValue}`}
                                            />
                                        </div>
                                    ) : (
                                        <img src="/assets/images/empty.png" alt="empty" />
                                    )}
                                </Droppable>
                            })}
                            </div>
                        </div>

                        <div className="songWrap">
                            <div className="songSwitch">
                                <div className={`songSwitchPre ${currentSongIndex === 0 ? 'hidden' : ''}`} onClick={() => switchSong('prev')}>
                                    <img src="/assets/icons/songswitchleft.svg" alt="" />
                                </div>
                    </div>
                    <div className="song">
                        <div className="title">
                                    {currentSong && (
                                        <>
                                            <div className="titleEn">{`《${currentSong.englishName}》`}</div>
                                            <div className="titleCn">{`${currentSong.chineseName}`}</div>
                                        </>
                                    )}
                                    <div className="btns">
                                        <div 
                                            className={`delete ${isDeleteMode ? 'active' : ''}`} 
                                            onClick={handleDeleteClick}
                                        >
                                            <img src="/assets/icons/delete.svg" alt="删除" />
                                        </div>

                                        {currentSong?.type === 'oc' ? (
                                            <>
                                                <div 
                                                    className={`record ${isRecordMode ? 'active' : ''}`} 
                                                    onClick={handleRecordClick}
                                                >
                                                    <img src="/assets/icons/record.png" alt="录音" />
                                                </div>

                                                <div 
                                                    className={`photo ${isPhotoMode ? 'active' : ''}`} 
                                                    onClick={handlePhotoClick}
                                                >
                                                    <img src="/assets/icons/capture.png" alt="拍摄" />
                                                </div>
                                            </>
                                        ) : null}

                                    </div>
                        </div>

            
                        {currentSong?.type === 'oc' ? (
                            <div className="ocTracks">
                                <OcTrackList 
                                    tracks={currentSong?.ocTracks.map(track => ({
                                        id: track.hashValue,
                                        hash: track.hashValue,
                                        top_color: track.topColor || '#ffffff',
                                        bottom_color: track.bottomColor || '#ffffff',
                                        isNew: !JSON.parse(localStorage.getItem('usedtrack') || '[]').includes(track.hashValue),
                                        type: 'oc',
                                        link: track.link,
                                        isRecordable: track.isRecordable,
                                        firstRecordedAt: track.firstRecordedAt,
                                        hasOcImage: track.hasOcImage || false
                                    })) || []}
                                    onTrackClick={(track) => {
                                        if (isRecordMode && currentSong) {
                                            const ocTrack = currentSong.ocTracks.find(t => t.hashValue === track.hash);
                                            if (ocTrack && track.isRecordable) {
                                                handleOcTrackClick(ocTrack);
                                            } else {
                                                message.error('无法录制该音轨');
                                            }
                                        } else if (isPhotoMode ) {
                                            // 在拍照模式下，检查hash是否被锁止
                                            if (isHashLocked(track.hash)) {
                                                message.warning('正在处理中，请稍后再试');
                                                return;
                                            }

                                            if (track.hasOcImage) {
                                                message.error('该音轨已生成OC图');
                                            }else{
                                                // 显示检查中的提示
                                                message.loading('检查是否已有候选图片...', 0);
                                                
                                                // 检查是否已有候选图片
                                                checkExistingImages(track.hash).then(existingImages => {
                                                    // 隐藏加载提示
                                                    message.destroy();
                                                    
                                                    if (existingImages.length === 2) {
                                                        // 如果已有两张候选图片，直接显示选择界面
                                                        setGeneratedImages(existingImages);
                                                        setSelectedTrackHash(track.hash);
                                                        setIsPhotoMode(false); // 关闭拍照模式
                                                        message.success('发现已有候选图片，请选择一张生成动画');
                                                    } else {
                                                        // 如果没有候选图片，显示相机组件
                                                        setSelectedTrackHash(track.hash);
                                                    }
                                                }).catch(error => {
                                                    // 隐藏加载提示并处理错误
                                                    message.destroy();
                                                    console.error('检查候选图片失败:', error);
                                                    // 即使检查失败，也显示相机组件
                                                    setSelectedTrackHash(track.hash);
                                                });
                                            }
                                        } else {
                                            // 非录音模式下的点击处理逻辑
                                            // 检查是否有候选图片，如果有则直接显示选择界面
                                            if (tracksWithCandidates.has(track.hash)) {
                                                // 显示检查中的提示
                                                message.loading('获取候选图片...', 0);
                                                
                                                checkExistingImages(track.hash).then(existingImages => {
                                                    // 隐藏加载提示
                                                    message.destroy();
                                                    
                                                    if (existingImages.length === 2) {
                                                        // 直接显示选择界面
                                                        setGeneratedImages(existingImages);
                                                        setSelectedTrackHash(track.hash);
                                                        message.success('请选择一张图片生成动画');
                                                    } else {
                                                        message.error('候选图片获取失败');
                                                    }
                                                }).catch(error => {
                                                    // 隐藏加载提示并处理错误
                                                    message.destroy();
                                                    console.error('获取候选图片失败:', error);
                                                    message.error('获取候选图片失败');
                                                });
                                            } else if (!track.link) {
                                                message.error('还不能使用，需要先录音');
                                            }
                                        }
                                    }}
                                    deleteMode={isDeleteMode}
                                    recordMode={isRecordMode}
                                    photoMode={isPhotoMode}
                                    onDelete={handleDeleteOcTrack}
                                    lockedHashes={lockedHashes}
                                    tracksWithCandidates={tracksWithCandidates}
                                    generatingAnimationHashes={generatingAnimationHashes}
                                />
                            </div>
                        ) : (
                            <div className="songTracks">
                                <TrackList 
                                    tracks={currentSong?.tracks.beats || []}
                                    type="beat"
                                    songName={currentSong?.englishName || ''}
                                    isDeleteMode={isDeleteMode}
                                    onDeleteTrack={handleDeleteTrack}
                                />
                                
                                <TrackList 
                                    tracks={currentSong?.tracks.effects || []}
                                    type="effect"
                                    songName={currentSong?.englishName || ''}
                                    isDeleteMode={isDeleteMode}
                                    onDeleteTrack={handleDeleteTrack}
                                />
                                
                                <TrackList 
                                    tracks={currentSong?.tracks.melodys || []}
                                    type="melody"
                                    songName={currentSong?.englishName || ''}
                                    isDeleteMode={isDeleteMode}
                                    onDeleteTrack={handleDeleteTrack}
                                />
                                
                                <TrackList 
                                    tracks={currentSong?.tracks.vocals || []}
                                    type="vocal"
                                    songName={currentSong?.englishName || ''}
                                    isDeleteMode={isDeleteMode}
                                    onDeleteTrack={handleDeleteTrack}
                                />
                            </div>
                        )}
                            </div>
                    <div className="songSwitch">
                        <div className={`songSwitchNext ${currentSongIndex === (songs[bpm]?.length - 1) ? 'hidden' : ''}`} onClick={() => switchSong('next')}>
                            <img src="/assets/icons/songswitchright.svg" alt="" />
                        </div>
                    </div>          
                </div>
            </div>
            </DndContext>
            </div> 
            <div className="readerWrapper" style={{ maxWidth: `${maxWidth}px` }}>
                <div id="reader"></div>
            </div>

            {isScanning && (
                <div className="scan-overlay">
                    <div className="scan-container">
                        <button className="close-button" onClick={handleCloseScan}>
                            <img src="/assets/icons/close.svg" alt="关闭" />
                        </button>
                    </div>
                </div>
            )}
            
            {/* 全局悬浮排行榜按钮 */}

            <a href="/ranking" className="global-ranking-btn">
                <img src="/assets/icons/ranking.svg" alt="排行榜" />
                <span>OC排名</span>
            </a>



        </div>
);
}

export default Homepage;